"use client";

import { useState } from "react";
import { Badge } from "@modernatc/ui/badge";
import { Label } from "@modernatc/ui/label";
import {
  CATEGORY_HINTS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  IN_TRAIL_NM,
  RUNWAY_MINUTES,
  requiredSeparation,
  type WakeCategory,
} from "@/lib/wake-separation";

/**
 * Wake turbulence separation lookup.
 *
 * 123atc has none of this. The .65 wake matrix is the kind of thing
 * working controllers rehearse cold — wrong separation here is an
 * actual safety incident. This tool gives the answer instantly with
 * the source paragraph cited, and also shows the full matrix so a
 * student can study the pattern (not just memorize one cell).
 */
export function WakeSeparationLookup() {
  const [leading, setLeading] = useState<WakeCategory>("heavy");
  const [trailing, setTrailing] = useState<WakeCategory>("small");
  const [scenario, setScenario] = useState<"in-trail" | "runway">("in-trail");

  const sep = requiredSeparation(leading, trailing, scenario);

  return (
    <div className="space-y-8">
      {/* Picker */}
      <div className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-6">
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_1fr]">
          <div>
            <Label className="text-xs">Leading aircraft</Label>
            <CategorySelect value={leading} onChange={setLeading} />
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {CATEGORY_HINTS[leading]}
            </p>
          </div>
          <div>
            <Label className="text-xs">Trailing aircraft</Label>
            <CategorySelect value={trailing} onChange={setTrailing} />
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {CATEGORY_HINTS[trailing]}
            </p>
          </div>
          <div>
            <Label className="text-xs">Scenario</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Toggle
                active={scenario === "in-trail"}
                onClick={() => setScenario("in-trail")}
                label="In-trail (radar)"
              />
              <Toggle
                active={scenario === "runway"}
                onClick={() => setScenario("runway")}
                label="Same runway (depart)"
              />
            </div>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              In-trail uses NM; runway-departure uses minutes.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-[var(--radius-md)] border border-[var(--color-amber)]/30 bg-[rgb(212_158_82_/_0.06)] p-5">
          <div className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
            Required separation
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            {sep ? (
              <>
                <span className="text-4xl font-semibold text-[var(--color-amber-bright)]">
                  {sep.value}
                </span>
                <span className="text-lg text-[var(--color-ink)]">{sep.unit}</span>
                <Badge variant="muted" className="ml-2">
                  Wake minimum
                </Badge>
              </>
            ) : (
              <>
                <span className="text-4xl font-semibold text-[var(--color-ink)]">
                  {scenario === "in-trail" ? "3" : "—"}
                </span>
                <span className="text-lg text-[var(--color-ink-dim)]">
                  {scenario === "in-trail" ? "NM (standard radar)" : "no wake min"}
                </span>
              </>
            )}
          </div>
          <p className="mt-3 text-sm text-[var(--color-ink-dim)]">
            {sep ? (
              <>
                {CATEGORY_LABELS[trailing]} behind {CATEGORY_LABELS[leading]}:{" "}
                {scenario === "in-trail" ? "in-trail radar" : "same-runway departure"}{" "}
                wake minimum applies.
              </>
            ) : (
              <>
                No wake minimum required. {scenario === "in-trail"
                  ? "Standard 3 NM radar separation suffices."
                  : "Same-runway separation governed by normal departure rules."}
              </>
            )}
          </p>
          <p className="mt-3 text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
            JO 7110.65 § {scenario === "in-trail" ? "5-5-4" : "3-9-6"}
          </p>
        </div>
      </div>

      {/* Full matrix */}
      <div>
        <h2 className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          Full matrix · {scenario === "in-trail" ? "In-trail (NM)" : "Same runway (min)"}
        </h2>
        <div className="mt-3 overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)]">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--color-panel-deep)]">
                <th className="border-b border-[var(--color-panel-edge)] px-3 py-3 text-left text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
                  Leading ↓ / Trailing →
                </th>
                {CATEGORY_ORDER.map((cat) => (
                  <th
                    key={cat}
                    className="border-b border-l border-[var(--color-panel-edge)] px-3 py-3 text-center text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]"
                  >
                    {CATEGORY_LABELS[cat]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((leadingCat) => (
                <tr key={leadingCat}>
                  <th className="border-b border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)]/60 px-3 py-3 text-left font-medium text-[var(--color-ink)]">
                    {CATEGORY_LABELS[leadingCat]}
                  </th>
                  {CATEGORY_ORDER.map((trailingCat) => {
                    const matrix = scenario === "in-trail" ? IN_TRAIL_NM : RUNWAY_MINUTES;
                    const v = matrix[leadingCat]?.[trailingCat];
                    const isActive = leadingCat === leading && trailingCat === trailing;
                    const unit = scenario === "in-trail" ? "NM" : "min";
                    return (
                      <td
                        key={trailingCat}
                        className={`border-b border-l border-[var(--color-panel-edge)] px-3 py-3 text-center text-mono ${
                          isActive
                            ? "bg-[var(--color-amber)]/15 font-semibold text-[var(--color-amber-bright)]"
                            : typeof v === "number"
                            ? "text-[var(--color-ink)]"
                            : "text-[var(--color-ink-muted)]"
                        }`}
                      >
                        {typeof v === "number" ? `${v} ${unit}` : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
          A dash (—) means no wake-specific minimum;{" "}
          {scenario === "in-trail"
            ? "standard 3 NM radar separation applies."
            : "normal same-runway departure rules apply."}
        </p>
      </div>
    </div>
  );
}

function CategorySelect({
  value,
  onChange,
}: {
  value: WakeCategory;
  onChange: (v: WakeCategory) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as WakeCategory)}
      className="mt-1 flex h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] px-3 text-sm text-[var(--color-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--color-amber)]/40"
    >
      {CATEGORY_ORDER.map((cat) => (
        <option key={cat} value={cat}>
          {CATEGORY_LABELS[cat]}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[var(--radius-sm)] border px-3 py-2 text-xs transition ${
        active
          ? "border-[var(--color-amber)] bg-[var(--color-amber)]/15 text-[var(--color-amber-bright)]"
          : "border-[var(--color-panel-edge)] bg-[var(--color-panel)] text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)]/40"
      }`}
    >
      {label}
    </button>
  );
}
